import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Check,
  Close,
  ChatBubbleOutline,
  ExpandMore,
  ChevronRight,
  ChevronLeft,
  History,
  DeleteOutline,
  NotificationsActiveOutlined,
  Undo,
  UploadFileOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { buildUploadUrl, downloadDocument, downloadFileFromPath, fetchDocumentBlobUrl, fetchFileBlobUrl, isFallbackDocument } from '../../utils/fileAccess';
import DepartmentSelect from '../../components/Departments/DepartmentSelect';
import useSetupConfig from '../../hooks/useSetupConfig';
import StatusLabel from '../../components/Status/StatusLabel';
import ContractActivity from '../../components/Contracts/ContractActivity';
import { showsContractComments } from '../../components/Contracts/activityTimeline';
import { canDeleteSupplier } from '../../utils/rolePermissions';
import { canSetApplicability, APPLICABILITY_LABEL } from '../../utils/contractApplicability';
import { canChangeEntityType, entityTypeLabel, ENTITY_TYPE_OPTIONS } from '../../utils/entityType';
import { canRecallRejection } from '../../utils/documentRecall';
import { canUploadCountersignedNda, countersignedNdaOf, isPdf, uploaderName } from '../../components/Documents/ndaCopies';

// Supplier.entityType is a snake_case enum; entity_types setup config is keyed by
// display name. Mirrors ENUM_TO_ENTITY_NAME in server/utils/documentRequirements.js.
const ENTITY_TYPE_LABELS = {
  private_company: 'Private/Public Company',
  public_company: 'Private/Public Company',
  partnership: 'Partnerships',
  foreign_company: 'Foreign Company',
  individual: 'Individual/Sole Proprietor',
  trust: 'Trust',
};

// Shown under "Professional Services" rather than with the entity checklist.
const SERVICE_DOC_TYPES = new Set(['practicing_certificate', 'member_resume']);

const SupplierDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: entityTypeItems } = useSetupConfig('entity_types');
  const theme = useTheme();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '' });
  const [rejectDocDialog, setRejectDocDialog] = useState({ open: false, doc: null });
  const [rejectDocReason, setRejectDocReason] = useState('');
  const [rejectDocSaving, setRejectDocSaving] = useState(false);
  const [comments, setComments] = useState('');
  const [departmentDraft, setDepartmentDraft] = useState('');
  const [departmentEditing, setDepartmentEditing] = useState(false);
  const [departmentSaving, setDepartmentSaving] = useState(false);
  const [entityTypeDialog, setEntityTypeDialog] = useState({ open: false, entityType: '', reason: '' });
  const [entityTypeSaving, setEntityTypeSaving] = useState(false);
  const [recallDocDialog, setRecallDocDialog] = useState({ open: false, doc: null, reason: '' });
  const [recallDocSaving, setRecallDocSaving] = useState(false);
  const [departmentHistory, setDepartmentHistory] = useState([]);
  const [departmentHistoryOpen, setDepartmentHistoryOpen] = useState(false);
  const [expanded, setExpanded] = useState(['basicInformation']);
  const [ndaUploading, setNdaUploading] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);
  const [fileViewerName, setFileViewerName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindNote, setRemindNote] = useState('');
  const [reminding, setReminding] = useState(false);
  // The dialog writes the note, then previews the exact email before it can be
  // sent. `remindPreview` is null while writing.
  const [remindPreview, setRemindPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    fetchSupplier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only procurement's options depend on a switch; nobody else needs the call.
  useEffect(() => {
    if (user?.role !== 'procurement') return;
    api.get('/settings/permissions')
      .then(({ data }) => setPermissions(data.data))
      .catch(() => setPermissions(null));
  }, [user?.role]);

  const fetchSupplier = async () => {
    try {
      const response = await api.get(`/suppliers/${id}`);
      const supplierData = response.data.data;
      setSupplier(supplierData);
    } catch (error) {
      toast.error('Failed to load supplier details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatApplicationId = (id, createdAt) => {
    if (!id) return 'APP-2025-001';
    const year = createdAt ? new Date(createdAt).getFullYear() : new Date().getFullYear();
    // Extract last 3 digits or use a sequential number
    const shortId = id.toString().slice(-3).padStart(3, '0');
    return `APP-${year}-${shortId}`;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      draft: 'Draft',
      submitted: 'Pending Approval',
      pending_procurement: 'Pending Approval',
      pending_legal: 'Pending Legal Approval',
      under_review: 'Under Review',
      approved: 'Approved',
      pending_contract_upload: 'Prequalified and Pending Contract Upload',
      completed: 'Completed',
      rejected: 'Rejected',
      more_info_required: 'Requested More Info',
    };
    return statusMap[status] || status;
  };


  const formatLegalNature = (legalNature) => {
    const map = {
      'state_owned': 'State-Owned',
      'ngo': 'NGO',
      'foundation': 'Foundation',
      'association': 'Association',
      'company': 'Company',
      'partnership': 'Partnership',
      'foreign_company': 'Foreign Company',
      'individual': 'Individual',
      'trust': 'Trust',
      'other': 'Other',
    };
    return map[legalNature] || legalNature;
  };

  const formatServiceType = (serviceType) => {
    if (!serviceType) return '-';

    // Handle array (if serviceTypes is an array)
    if (Array.isArray(serviceType) && serviceType.length > 0) {
      return serviceType[0]; // Return first item if it's an array
    }

    // Map enum values to display names
    const map = {
      'professional_services': 'Professional Services',
      'digital_services': 'Digital Services',
      'physical_goods': 'Physical Goods',
      'other': 'Other',
      // Also handle direct string values from serviceTypes field
      'Professional Services': 'Professional Services',
      'Goods Supply': 'Goods Supply',
      'Services': 'Services',
      'Consultancy': 'Consultancy',
      'Construction': 'Construction',
      'IT Services': 'IT Services',
      'Maintenance & Repair': 'Maintenance & Repair',
    };

    if (map[serviceType]) {
      return map[serviceType];
    }

    // If it's already a formatted string (doesn't contain underscore)
    if (typeof serviceType === 'string' && !serviceType.includes('_')) {
      return serviceType;
    }

    // Fallback: format the enum value
    return serviceType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address || null;
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.country) parts.push(address.country);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatDocumentType = (type) => {
    const map = {
      'certificate_of_incorporation': 'Certificate of Incorporation',
      'cr12': 'CR12',
      'pin_certificate': 'KRA Pin Certificate',
      'company_profile': 'Company Profile',
      'bank_reference': 'Bank Reference Letter',
      'etims_registration': 'Proof of registration on E-tims',
      'practicing_certificate': 'Practicing Certificate',
      'member_resume': 'Resume',
      'directors_id': 'Directors ID',
      'audited_financials': 'Audited Financial Statements',
      'nda': 'Executed Non-Disclosure Agreement',
      'nda_countersigned': 'Countersigned Non-Disclosure Agreement',
    };
    return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getNdaDocument = () => {
    if (!supplier) return null;

    // Check documents array first
    if (supplier.documents && Array.isArray(supplier.documents)) {
      const ndaDoc = supplier.documents.find(doc => doc.documentType === 'nda');
      if (ndaDoc) return ndaDoc;
    }

    // Fallback: check ndaDocument field directly on supplier object
    if (supplier.ndaDocument && typeof supplier.ndaDocument === 'string') {
      return {
        _id: 'doc-nda',
        documentType: 'nda',
        originalName: 'Executed Non-Disclosure Agreement',
        fileSize: 0,
        fileName: supplier.ndaDocument,
      };
    }

    return null;
  };

  const handleViewFile = async (document) => {
    let url;
    const fileName = document.originalName || document.fileName || 'Document';

    if (isFallbackDocument(document)) {
      const filePath = document.fileName || document.originalName;
      try {
        url = await fetchFileBlobUrl(buildUploadUrl(filePath, id));
      } catch (error) {
        toast.error(error.message || 'Failed to open document');
        return;
      }
    } else {
      try {
        url = await fetchDocumentBlobUrl(document._id);
      } catch (error) {
        // Fallback: try serving directly from static uploads path
        if (document.fileName) {
          const supplierId = document.supplier?._id || document.supplier || id;
          const directUrl = buildUploadUrl(document.fileName, supplierId);
          try {
            url = await fetchFileBlobUrl(directUrl);
          } catch {
            toast.error(error.response?.data?.message || 'Document file is not available on the server.');
            return;
          }
        } else {
          toast.error(error.response?.data?.message || 'Failed to open document');
          return;
        }
      }
    }

    setFileViewerUrl(url);
    setFileViewerName(fileName);
    setFileViewerOpen(true);
  };

  const handleCountersignedNdaUpload = async (event) => {
    const file = event.target.files?.[0];
    // Clear it so picking the same file again after a failure still fires.
    event.target.value = '';
    if (!file) return;
    if (!isPdf(file)) {
      toast.error('Upload the countersigned NDA as a PDF.');
      return;
    }
    const replacing = !!countersignedNdaOf(supplier);
    const form = new FormData();
    form.append('document', file);
    try {
      setNdaUploading(true);
      await api.post(`/documents/supplier/${id}/countersigned-nda`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(replacing ? 'Countersigned NDA replaced' : 'Countersigned NDA uploaded');
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload the countersigned NDA');
    } finally {
      setNdaUploading(false);
    }
  };

  const canDeleteSupplierNow = canDeleteSupplier(user, permissions, supplier);

  // A reminder is for an application waiting on the supplier. The server holds
  // the same rule, plus the HOD's department and the once-a-day limit.
  const REMIND_WAIT_MS = 24 * 60 * 60 * 1000;
  const canRemind = ['procurement', 'legal', 'management', 'super_admin'].includes(user?.role)
    && ['draft', 'more_info_required'].includes(supplier?.status);
  const lastReminder = supplier?.lastReminder?.sentAt ? supplier.lastReminder : null;
  const reminderCoolingDown = lastReminder
    && Date.now() - new Date(lastReminder.sentAt).getTime() < REMIND_WAIT_MS;
  const lastReminderText = lastReminder
    ? `Last reminded ${new Date(lastReminder.sentAt).toLocaleString()}${
      lastReminder.sentBy?.firstName
        ? ` by ${[lastReminder.sentBy.firstName, lastReminder.sentBy.lastName].filter(Boolean).join(' ')}`
        : ''}`
    : '';

  const closeRemind = () => {
    setRemindOpen(false);
    setRemindPreview(null);
  };

  const handlePreviewReminder = async () => {
    try {
      setPreviewing(true);
      const { data } = await api.post(`/suppliers/${id}/remind/preview`, { note: remindNote.trim() || undefined });
      // Remember which note this preview is of, so an edited note cannot be
      // sent on the strength of an older preview.
      setRemindPreview({ ...data.data, note: remindNote.trim() });
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to preview reminder');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendReminder = async () => {
    if (!remindPreview || remindPreview.note !== remindNote.trim()) return;
    try {
      setReminding(true);
      await api.post(`/suppliers/${id}/remind`, { note: remindNote.trim() || undefined });
      toast.success('Reminder sent to the supplier');
      closeRemind();
      setRemindNote('');
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reminder');
      if (error.response?.status === 429) {
        closeRemind();
        fetchSupplier();
      }
    } finally {
      setReminding(false);
    }
  };

  // The phrase the admin has to type back before the delete is allowed.
  //
  // This was supplierName alone, which is empty on a profile-only record — the
  // draft route stores '' for those. That rendered as `Type "" to confirm` and,
  // worse, satisfied the equality guard on an empty box, so the deliberate
  // friction disappeared on exactly the records most likely to be deleted.
  const deleteConfirmPhrase =
    supplier?.supplierName?.trim() || supplier?.applicationNumber?.trim() || 'DELETE';

  const handleDeleteSupplier = async () => {
    try {
      setDeleting(true);
      const { data } = await api.delete(`/suppliers/${id}/admin`, {
        data: { reason: deleteReason.trim() },
      });
      toast.success(data.message || 'Supplier deleted');
      navigate('/suppliers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete supplier');
      setDeleting(false);
    }
  };

  const handleCloseFileViewer = () => {
    if (fileViewerUrl?.startsWith('blob:')) {
      window.URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerOpen(false);
    setFileViewerUrl(null);
    setFileViewerName('');
  };

  const handleDownloadFile = async (document) => {

    if (isFallbackDocument(document)) {
      const filePath = document.fileName || document.originalName;
      try {
        await downloadFileFromPath(buildUploadUrl(filePath, id), document.originalName || filePath);
      } catch (error) {
        toast.error(error.message || 'Failed to download document');
      }
    } else {
      try {
        await downloadDocument(document);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to download document');
      }
    }
  };

  // The checklist for this supplier's entity type, from the same setup config the
  // wizard renders. This used to be two hand-written maps of field -> type -> label
  // kept in step with the wizard's own map by hand; they drifted, and the labels a
  // reviewer saw stopped matching the boxes the supplier filled.
  const entityDocFields = useMemo(() => {
    const found = entityTypeItems.find(
      (item) => item.name === ENTITY_TYPE_LABELS[supplier?.entityType] || item.name === supplier?.entityType
    );
    return found?.documents || [];
  }, [entityTypeItems, supplier?.entityType]);

  const getEntityDocuments = () => {
    if (!supplier) return [];

    const configuredTypes = new Set(
      entityDocFields.map((d) => d.documentType).filter(Boolean)
    );

    // Uploaded Document rows are the real record; prefer them.
    if (Array.isArray(supplier.documents) && supplier.documents.length > 0) {
      const apiDocs = supplier.documents.filter(
        (doc) => configuredTypes.has(doc.documentType) && !SERVICE_DOC_TYPES.has(doc.documentType)
      );
      if (apiDocs.length > 0) return apiDocs;
    }

    // Fallback: filenames written straight onto the supplier record.
    const entityDocs = [];
    for (const doc of entityDocFields) {
      if (SERVICE_DOC_TYPES.has(doc.documentType)) continue;
      const value = supplier[doc.field];

      if (doc.uploadType === 'multiple') {
        const files = Array.isArray(value) ? value : [];
        files.forEach((file, index) => {
          if (typeof file !== 'string' || !file.trim()) return;
          entityDocs.push({
            _id: `doc-${doc.field}-${index}`,
            documentType: doc.documentType,
            originalName: files.length > 1 ? `${doc.label} ${index + 1}` : doc.label,
            fileSize: 2400000,
            fileName: file,
          });
        });
        continue;
      }

      if (typeof value === 'string' && value.trim()) {
        entityDocs.push({
          _id: `doc-${doc.field}`,
          documentType: doc.documentType,
          originalName: doc.label,
          fileSize: 2400000,
          fileName: value,
        });
      }
    }

    return entityDocs;
  };

  const getServiceDocuments = () => {
    if (!supplier) return [];

    if (Array.isArray(supplier.documents) && supplier.documents.length > 0) {
      const apiDocs = supplier.documents.filter((doc) => SERVICE_DOC_TYPES.has(doc.documentType));
      if (apiDocs.length > 0) return apiDocs;
    }

    const serviceDocs = [];
    for (const doc of entityDocFields) {
      if (!SERVICE_DOC_TYPES.has(doc.documentType)) continue;
      const files = Array.isArray(supplier[doc.field]) ? supplier[doc.field] : [];
      files.forEach((file, index) => {
        if (typeof file !== 'string' || !file.trim()) return;
        serviceDocs.push({
          _id: `${doc.field}-${index}`,
          documentType: doc.documentType,
          originalName: files.length > 1 ? `${doc.label} ${index + 1}` : doc.label,
          fileSize: 2400000,
          fileName: file,
        });
      });
    }

    return serviceDocs;
  };

  const canEditDepartment = ['procurement', 'super_admin'].includes(user?.role);
  const canViewDepartmentHistory = user?.role === 'super_admin';

  const fetchDepartmentHistory = async () => {
    try {
      const res = await api.get('/audit-logs', {
        params: { entityType: 'Supplier', entityId: id, limit: 50 },
      });
      const rows = (res.data.data || []).filter((r) =>
        r.action === 'SUPPLIER_DEPARTMENT_ASSIGNED' || r.action === 'SUPPLIER_DEPARTMENT_CHANGED'
      );
      setDepartmentHistory(rows);
      setDepartmentHistoryOpen(true);
    } catch (error) {
      toast.error('Failed to load department history');
    }
  };

  const handleChangeEntityType = async () => {
    try {
      setEntityTypeSaving(true);
      const { data } = await api.put(`/suppliers/${id}/entity-type`, {
        entityType: entityTypeDialog.entityType,
        reason: entityTypeDialog.reason.trim(),
      });
      const missing = data.data?.missing || [];
      toast.success(missing.length > 0
        ? `${data.message}. The supplier has been told what this type still needs (${missing.length}).`
        : data.message);
      setEntityTypeDialog({ open: false, entityType: '', reason: '' });
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change the entity type');
    } finally {
      setEntityTypeSaving(false);
    }
  };

  const handleSaveDepartment = async () => {
    if (!departmentDraft) return;
    try {
      setDepartmentSaving(true);
      await api.put(`/suppliers/${id}/department`, { department: departmentDraft });
      toast.success(`Department set to ${departmentDraft}`);
      setDepartmentEditing(false);
      setDepartmentHistoryOpen(false);
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to set department');
    } finally {
      setDepartmentSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/approvals/${id}/approve`, { comments });
      toast.success('Application approved successfully');
      setActionDialog({ open: false, type: '' });
      setComments('');
      fetchSupplier();
      navigate('/tasks');
    } catch (error) {
      if (error.response?.data?.code === 'DEPARTMENT_REQUIRED') {
        toast.error('Assign this supplier to a department before approving.');
        setActionDialog({ open: false, type: '' });
        setDepartmentDraft('');
        setDepartmentEditing(true);
        setExpanded((prev) => prev.includes('basicInformation') ? prev : [...prev, 'basicInformation']);
        return;
      }
      toast.error(error.response?.data?.message || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await api.post(`/approvals/${id}/reject`, { comments });
      toast.success('Application rejected');
      setActionDialog({ open: false, type: '' });
      setComments('');
      fetchSupplier();
      navigate('/tasks');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    }
  };

  const handleRequestInfo = async () => {
    if (!comments.trim()) {
      toast.error('Please specify what information is needed');
      return;
    }
    try {
      await api.post(`/approvals/${id}/request-info`, { comments });
      toast.success('Information request sent');
      setActionDialog({ open: false, type: '' });
      setComments('');
      fetchSupplier();
      navigate('/tasks');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to request info');
    }
  };



  const handleRecall = async () => {
    if (!comments.trim()) {
      toast.error('Please give a reason for recalling this application');
      return;
    }
    try {
      const res = await api.post(`/approvals/${id}/recall`, { comments });
      toast.success(res.data?.message || 'Application recalled');
      setActionDialog({ open: false, type: '' });
      setComments('');
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to recall application');
    }
  };

  const handleContractApplicability = async (applicability) => {
    try {
      const res = await api.post(`/approvals/${id}/contract-applicability`, {
        applicability,
        reason: comments.trim() || undefined,
      });
      toast.success(res.data?.message || 'Contract applicability saved');
      setActionDialog({ open: false, type: '' });
      setComments('');
      fetchSupplier();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save contract applicability');
    }
  };

  // Procurement can take back a rejection — theirs or legal's.
  const canRecall = () => Boolean(supplier) && user.role === 'procurement' && supplier.status === 'rejected';

  const procurementStatuses = ['submitted', 'pending_procurement', 'more_info_required'];
  const legalStatuses = ['pending_legal'];

  const isMyTask = () => {
    if (!supplier) return false;
    if (!supplier.assignedTo) return false;
    const assignedId = supplier.assignedTo?._id || supplier.assignedTo;
    return String(assignedId) === String(user.id || user._id);
  };

  const canPickUp = () => {
    if (!supplier) return false;
    if (supplier.assignedTo) return false; // already picked up
    if (user.role === 'procurement') return procurementStatuses.includes(supplier.status);
    if (user.role === 'legal') return legalStatuses.includes(supplier.status);
    return false;
  };

  const canApprove = () => {
    if (!supplier) return false;
    if (user.role === 'procurement') {
      return procurementStatuses.includes(supplier.status) && isMyTask();
    }
    if (user.role === 'legal') {
      return legalStatuses.includes(supplier.status) && isMyTask();
    }
    return false;
  };

  // Rejecting one document is the light-touch alternative to bouncing the whole
  // application: the server hands it back to the supplier with this reason
  // attached, and returns it to this stage once they re-upload.
  const canRejectDocuments = () => {
    if (!supplier) return false;
    if (user.role === 'super_admin') return true;
    return canApprove();
  };

  const handleRejectDocument = async () => {
    const reason = rejectDocReason.trim();
    if (!reason) return;
    try {
      setRejectDocSaving(true);
      await api.put(`/documents/${rejectDocDialog.doc._id}/status`, {
        status: 'rejected',
        notes: reason,
      });
      toast.success('Document rejected — the supplier has been asked to replace it');
      setRejectDocDialog({ open: false, doc: null });
      setRejectDocReason('');
      fetchSupplier();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject document');
    } finally {
      setRejectDocSaving(false);
    }
  };

  const handleRecallDocRejection = async () => {
    const reason = recallDocDialog.reason.trim();
    if (!reason) return;
    try {
      setRecallDocSaving(true);
      const { data } = await api.post(`/documents/${recallDocDialog.doc._id}/recall-rejection`, { reason });
      toast.success(data.message || 'Document accepted');
      setRecallDocDialog({ open: false, doc: null, reason: '' });
      fetchSupplier();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to recall the rejection');
    } finally {
      setRecallDocSaving(false);
    }
  };

  const handlePickUp = async () => {
    try {
      await api.post(`/suppliers/${supplier._id}/assign`);
      toast.success('Task picked up — you now own this application');
      fetchSupplier();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to pick up task');
    }
  };

  const handleRelease = async () => {
    try {
      await api.post(`/suppliers/${supplier._id}/unassign`);
      toast.success('Task released back to the pool');
      fetchSupplier();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to release task');
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prevExpanded) => {
      if (isExpanded) {
        // Add panel to expanded array if not already present
        return prevExpanded.includes(panel) ? prevExpanded : [...prevExpanded, panel];
      } else {
        // Remove panel from expanded array
        return prevExpanded.filter((p) => p !== panel);
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Supplier not found</Typography>
      </Box>
    );
  }

  const applicationId = formatApplicationId(supplier._id, supplier.createdAt);
  const statusLabel = getStatusLabel(supplier.status);
  const showActions = canApprove();


  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#fff',
      p: { xs: 0, sm: 3, md: 4 },
    }}>
      {/* Application Header */}
      <Box sx={{
        mb: 3,
        px: { xs: 2, sm: 0 },
        pt: { xs: 2, sm: 0 },
      }}>
        {/* Mobile: Back button and header */}
        <Box sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: '#111827',
              p: 1,
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#111827',
              flex: 1,
            }}
          >
            {applicationId}
          </Typography>
          <StatusLabel kind="application" status={supplier.status} label={statusLabel} />
        </Box>

        {/* Desktop: Application ID and Status */}
        <Box sx={{
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Left side - Application ID and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: '20px',
                color: '#111827',
              }}
            >
              {applicationId}
            </Typography>
            <StatusLabel kind="application" status={supplier.status} label={statusLabel} />
          </Box>

          {/* Right side - Action Buttons (Desktop only) */}
          {canPickUp() && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Button
                variant="outlined"
                onClick={handlePickUp}
                sx={{
                  borderColor: '#578A18',
                  color: '#578A18',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#f0fdf4', borderColor: '#467014' },
                }}
              >
                Pick Up Task
              </Button>
            </Box>
          )}
          {canDeleteSupplierNow && (
            <Box sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                startIcon={<DeleteOutline />}
                onClick={() => {
                  setDeleteReason('');
                  setDeleteConfirmName('');
                  setDeleteDialogOpen(true);
                }}
                sx={{
                  borderColor: '#fecaca',
                  color: '#b91c1c',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#fef2f2', borderColor: '#f87171' },
                }}
              >
                Delete Application
              </Button>
            </Box>
          )}
          {canRemind && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="outlined"
                startIcon={<NotificationsActiveOutlined />}
                onClick={() => {
                  setRemindNote('');
                  setRemindPreview(null);
                  setRemindOpen(true);
                }}
                disabled={Boolean(reminderCoolingDown)}
                title={reminderCoolingDown ? 'One reminder per supplier per day' : undefined}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                }}
              >
                Send Reminder
              </Button>
              {lastReminderText && (
                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>{lastReminderText}</Typography>
              )}
            </Box>
          )}
          {canSetApplicability(user, supplier) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                Contract: {APPLICABILITY_LABEL[supplier.contractApplicability] || 'not yet decided'}
              </Typography>
              {supplier.contractApplicability !== 'applicable' && (
                <Button
                  variant="outlined"
                  onClick={() => setActionDialog({ open: true, type: 'contract_applicable' })}
                  sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                }}
                >
                  Contract Applicable
                </Button>
              )}
              {supplier.status === 'pending_contract_upload' && (
                <Button
                  variant="outlined"
                  onClick={() => setActionDialog({ open: true, type: 'contract_not_applicable' })}
                  sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                }}
                >
                  Contract Not Applicable
                </Button>
              )}
            </Box>
          )}
          {canRecall() && (
            <Box sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                startIcon={<Undo />}
                onClick={() => setActionDialog({ open: true, type: 'recall' })}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                }}
              >
                Recall Application
              </Button>
            </Box>
          )}
          {isMyTask() && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Button
                variant="outlined"
                onClick={handleRelease}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#f9fafb', borderColor: '#9ca3af' },
                }}
              >
                Release Task
              </Button>
            </Box>
          )}
          {showActions && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Check sx={{ color: '#fff' }} />}
                onClick={() => setActionDialog({ open: true, type: 'approve' })}
                sx={{
                  backgroundColor: theme.palette.green.main,
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.green.hover,
                    boxShadow: 'none',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#fff',
                  },
                }}
              >
                Approve Application
              </Button>
              <Button
                variant="contained"
                startIcon={<Close />}
                onClick={() => setActionDialog({ open: true, type: 'reject' })}
                sx={{
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#dc2626',
                    boxShadow: 'none',
                  },
                }}
              >
                Reject Application
              </Button>
              <Button
                variant="outlined"
                startIcon={<ChatBubbleOutline />}
                onClick={() => setActionDialog({ open: true, type: 'request_info' })}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    bgcolor: '#f9fafb',
                  },
                }}
              >
                Request More Information
              </Button>
            </Box>
          )}
        </Box>

        {/* New Application Tag - Hidden on mobile (shown in Basic Information) */}
        <Chip
          label="New Application"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            bgcolor: '#f3f4f6',
            color: '#374151',
            fontSize: '12px',
            fontWeight: 400,
            height: '24px',
            borderRadius: '12px',
            '& .MuiChip-label': {
              px: 1.5,
            },
          }}
        />
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3} sx={{ maxWidth: '1400px', mx: 'auto' }}>
        {/* Left Side - Application Details */}
        <Grid item xs={12} md={12}>
          <Box sx={{
            border: '1px solid #e5e7eb',
            borderRadius: { xs: 0, sm: '8px' },
            overflow: 'hidden',
          }}>
            {/* Basic Information */}
            <Accordion
              expanded={expanded.includes('basicInformation')}
              onChange={handleAccordionChange('basicInformation')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('basicInformation') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Basic Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                {/* Request Type - Mobile only */}
                <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: 500,
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Request Type
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#111827',
                      fontWeight: 400,
                    }}
                  >
                    New Application
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Company Name
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.supplierName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Registered Country
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.companyPhysicalAddress?.country || supplier.registeredCountry || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Company Registration Number
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.companyRegistrationNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Company Email Address
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.companyEmail || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Company Website
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.companyWebsite || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Legal Nature of Entity
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {formatLegalNature(supplier.legalNature) || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Physical Address
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {formatAddress(supplier.companyPhysicalAddress) || supplier.physicalAddress || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Postal Address
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.postalAddress || supplier.companyPhysicalAddress?.postalCode || '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ color: '#6b7280', fontSize: '12px', mb: 0.5 }}>
                        Owning department
                      </Typography>
                      <Typography sx={{ color: '#111827', fontSize: '14px' }}>
                        {supplier?.department || (
                          <Typography component="span" sx={{ color: '#b45309', fontSize: '14px' }}>
                            Not assigned — required before approval
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {(canEditDepartment || canViewDepartmentHistory) && !departmentEditing && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canEditDepartment && (
                          <Button
                            size="small"
                            onClick={() => { setDepartmentDraft(supplier?.department || ''); setDepartmentEditing(true); }}
                            sx={{ textTransform: 'none', fontSize: '13px', color: '#374151' }}
                          >
                            {supplier?.department ? 'Change' : 'Assign'}
                          </Button>
                        )}
                        {canViewDepartmentHistory && (
                          <Button
                            size="small"
                            onClick={fetchDepartmentHistory}
                            sx={{ textTransform: 'none', fontSize: '13px', color: '#374151' }}
                          >
                            History
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>

                  {departmentEditing && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 220, flex: 1, maxWidth: 320 }}>
                        <DepartmentSelect value={departmentDraft} onChange={setDepartmentDraft} />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!departmentDraft || departmentSaving}
                        onClick={handleSaveDepartment}
                        sx={{ textTransform: 'none', fontSize: '13px', bgcolor: '#578A18', '&:hover': { bgcolor: '#467014' } }}
                      >
                        {departmentSaving ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="small"
                        disabled={departmentSaving}
                        onClick={() => setDepartmentEditing(false)}
                        sx={{ textTransform: 'none', fontSize: '13px', color: '#374151' }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}

                  {departmentHistoryOpen && canViewDepartmentHistory && (
                    <Box sx={{ mt: 2 }}>
                      {departmentHistory.length === 0 ? (
                        <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>
                          No department changes recorded.
                        </Typography>
                      ) : (
                        departmentHistory.map((row) => (
                          <Typography key={row._id} sx={{ color: '#4b5563', fontSize: '13px', mb: 0.5 }}>
                            {row.details?.from ? `${row.details.from} → ${row.details.to}` : `Assigned to ${row.details?.to}`}
                            {' · '}{row.performedByName || 'System'}
                            {' · '}{new Date(row.createdAt).toLocaleString('en-GB')}
                          </Typography>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>


            {/* Contact Details */}
            <Accordion
              expanded={expanded.includes('contactDetails')}
              onChange={handleAccordionChange('contactDetails')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('contactDetails') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Contact Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Full Name
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.authorizedPerson?.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Relationship
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.authorizedPerson?.relationship || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.authorizedPerson?.email || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Phone
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.authorizedPerson?.phone || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      ID/Passport Number
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.authorizedPerson?.idPassportNumber || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Entity Details */}
            <Accordion
              expanded={expanded.includes('entityDetails')}
              onChange={handleAccordionChange('entityDetails')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('entityDetails') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Entity Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Entity Type
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#111827',
                          fontWeight: 400,
                        }}
                      >
                        {entityTypeLabel(supplier.entityType)}
                      </Typography>
                      {canChangeEntityType(user, supplier) && (
                        <Button
                          size="small"
                          onClick={() => setEntityTypeDialog({ open: true, entityType: supplier.entityType || '', reason: '' })}
                          sx={{ textTransform: 'none', fontSize: '13px', minWidth: 0, p: '2px 8px' }}
                        >
                          Change
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* Entity Documents Section */}
                {getEntityDocuments().length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 2,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Attached Documents
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {getEntityDocuments().map((doc) => (
                        <Box
                          key={doc._id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            bgcolor: '#fff',
                          }}
                        >
                          <Box
                            component="img"
                            src="/images/File.svg"
                            alt="File icon"
                            sx={{ width: 24, height: 24 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 400,
                                mb: 0.5,
                              }}
                            >
                              {formatDocumentType(doc.documentType)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: 400,
                              }}
                            >
                              PDF • {formatFileSize(doc.fileSize)}
                            </Typography>
                            {doc.status === 'rejected' && (
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: '#b91c1c',
                                  fontWeight: 500,
                                  mt: 0.5,
                                }}
                              >
                                Rejected{doc.notes ? ` — ${doc.notes}` : ''}
                              </Typography>
                            )}
                            {doc.status !== 'rejected' && doc.rejectionRecall?.reason && (
                              <Typography sx={{ fontSize: '12px', color: '#b45309', fontWeight: 500, mt: 0.5 }}>
                                Accepted as an exception — {doc.rejectionRecall.reason}
                              </Typography>
                            )}
                          </Box>
                          {/* Fallback rows are synthesised from filename fields on
                              the supplier, not real Document records, so there is
                              nothing for the status endpoint to act on. */}
                          {canRejectDocuments() && !isFallbackDocument(doc) && doc.status !== 'rejected' && (
                            <Button
                              size="small"
                              onClick={() => {
                                setRejectDocReason('');
                                setRejectDocDialog({ open: true, doc });
                              }}
                              sx={{
                                textTransform: 'none',
                                fontSize: '13px',
                                color: '#b91c1c',
                                minWidth: 'auto',
                                px: 1,
                                '&:hover': { backgroundColor: '#fef2f2' },
                              }}
                            >
                              Reject
                            </Button>
                          )}
                          {!isFallbackDocument(doc) && canRecallRejection(user, doc, supplier.documents || []) && (
                            <Button
                              size="small"
                              onClick={() => setRecallDocDialog({ open: true, doc, reason: '' })}
                              sx={{
                                textTransform: 'none',
                                fontSize: '13px',
                                color: '#b45309',
                                minWidth: 'auto',
                                px: 1,
                                '&:hover': { backgroundColor: '#fffbeb' },
                              }}
                            >
                              Recall rejection
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleViewFile(doc)}
                            sx={{
                              color: '#6b7280',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#374151'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src="/images/eye.svg"
                              alt="View icon"
                              sx={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(doc)}
                            sx={{
                              color: '#6b7280',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#374151'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src="/images/Download.svg"
                              alt="Download icon"
                              sx={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Types of Services Being Offered Section */}
                <Box sx={{ mt: 4 }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: 500,
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Types of Services Being Offered
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#111827',
                      fontWeight: 400,
                      mb: 3,
                    }}
                  >
                    {(supplier.serviceTypes === 'Other' || supplier.serviceType === 'other' || supplier.serviceType === 'Other')
                      ? (supplier.serviceTypesOther || 'Other')
                      : formatServiceType(supplier.serviceTypes || supplier.serviceType)}
                  </Typography>
                </Box>

                {/* Service Documents Section */}
                {getServiceDocuments().length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 2,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Attached Documents
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {getServiceDocuments().map((doc, index) => (
                        <Box
                          key={doc._id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            bgcolor: '#fff',
                          }}
                        >
                          <Box
                            component="img"
                            src="/images/File.svg"
                            alt="File icon"
                            sx={{ width: 24, height: 24 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 400,
                                mb: 0.5,
                              }}
                            >
                              {doc.documentType === 'member_resume'
                                ? `Resume ${getServiceDocuments().filter(d => d.documentType === 'member_resume').indexOf(doc) + 1}`
                                : formatDocumentType(doc.documentType)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: 400,
                              }}
                            >
                              PDF • {formatFileSize(doc.fileSize)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleViewFile(doc)}
                            sx={{
                              color: '#6b7280',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#374151'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src="/images/eye.svg"
                              alt="View icon"
                              sx={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(doc)}
                            sx={{
                              color: '#6b7280',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#374151'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src="/images/Download.svg"
                              alt="Download icon"
                              sx={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Payment Details */}
            <Accordion
              expanded={expanded.includes('paymentDetails')}
              onChange={handleAccordionChange('paymentDetails')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('paymentDetails') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                  Payment Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Bank Name
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400 }}>
                      {supplier.bankName === 'Other'
                        ? (supplier.bankNameOther || 'Other')
                        : (supplier.bankName || '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Account Number
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400 }}>
                      {supplier.accountNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Branch
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400 }}>
                      {supplier.branch || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Currency
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400 }}>
                      {supplier.currency || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Credit Period
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400 }}>
                      {supplier.creditPeriod ? `${supplier.creditPeriod} Days` : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Source of Funds Declaration */}
            <Accordion
              expanded={expanded.includes('sourceOfFunds')}
              onChange={handleAccordionChange('sourceOfFunds')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('sourceOfFunds') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Source of Funds Declaration
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Source of Wealth
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.sourceOfWealth === 'Other'
                        ? (supplier.sourceOfWealthOther || 'Other')
                        : supplier.sourceOfWealth
                          || (supplier.sourceOfFunds?.source
                            ? supplier.sourceOfFunds.source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                            : '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Declarant Full Name
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.sourceOfFunds?.declarantName || supplier.declarantFullName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Declarant Capacity
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.sourceOfFunds?.declarantCapacity || supplier.declarantCapacity || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      ID/Passport Number
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.sourceOfFunds?.declarantIdPassport || supplier.declarantIdPassport || '-'}
                    </Typography>
                  </Grid>
                  {supplier.sourceOfFunds?.declarationDate && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#6b7280',
                          fontWeight: 500,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Declaration Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#111827',
                          fontWeight: 400,
                        }}
                      >
                        {new Date(supplier.sourceOfFunds.declarationDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Personal Information Processing Consent */}
            <Accordion
              expanded={expanded.includes('dataConsent')}
              onChange={handleAccordionChange('dataConsent')}
              sx={{
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0 !important',
                mb: 0,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded.includes('dataConsent') ? (
                    <ExpandMore sx={{ color: '#6b7280' }} />
                  ) : (
                    <ChevronRight sx={{ color: '#6b7280' }} />
                  )
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  minHeight: '56px',
                  '&.Mui-expanded': {
                    minHeight: '56px',
                    borderBottom: 'none',
                    pb: 2,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                  position: 'relative',
                  '&.Mui-expanded::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Personal Information Processing Consent
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Full Name of Declarant
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.declarantFullName || supplier.dataProcessingConsent?.consentorName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: 500,
                        mb: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#111827',
                        fontWeight: 400,
                      }}
                    >
                      {supplier.declarationDate
                        ? (() => {
                          const date = new Date(supplier.declarationDate);
                          const day = date.getDate();
                          const month = date.toLocaleDateString('en-GB', { month: 'long' });
                          const year = date.getFullYear();
                          const daySuffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
                          return `${day}${daySuffix} ${month}, ${year}`;
                        })()
                        : (supplier.dataProcessingConsent?.consentDate
                          ? (() => {
                            const date = new Date(supplier.dataProcessingConsent.consentDate);
                            const day = date.getDate();
                            const month = date.toLocaleDateString('en-GB', { month: 'long' });
                            const year = date.getFullYear();
                            const daySuffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
                            return `${day}${daySuffix} ${month}, ${year}`;
                          })()
                          : '-')}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Non-Disclosure Agreement: the supplier's signed copy and Betika's countersigned one */}
            {(getNdaDocument() || countersignedNdaOf(supplier) || canUploadCountersignedNda(user, supplier)) && (
              <Accordion
                expanded={expanded.includes('nda')}
                onChange={handleAccordionChange('nda')}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderRadius: '0 !important',
                  mb: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    expanded.includes('nda') ? (
                      <ExpandMore sx={{ color: '#6b7280' }} />
                    ) : (
                      <ChevronRight sx={{ color: '#6b7280' }} />
                    )
                  }
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    minHeight: '56px',
                    '&.Mui-expanded': { minHeight: '56px', borderBottom: 'none', pb: 2 },
                    '& .MuiAccordionSummary-content': { my: 0 },
                    position: 'relative',
                    '&.Mui-expanded::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: { xs: '16px', sm: '24px' },
                      right: { xs: '16px', sm: '24px' },
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      zIndex: 1,
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                    Non-Disclosure Agreement
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  {(() => {
                    const supplierNda = getNdaDocument();
                    const countersigned = countersignedNdaOf(supplier);
                    const canUpload = canUploadCountersignedNda(user, supplier);
                    const renderNdaRow = (doc, title, detail) => (
                      <Box
                        key={doc._id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          mb: 1.5,
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          bgcolor: '#fff',
                        }}
                      >
                        <Box component="img" src="/images/File.svg" alt="File icon" sx={{ width: 24, height: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400, mb: 0.5 }}>
                            {title}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                            {detail}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleViewFile(doc)}
                          sx={{ color: '#6b7280', '&:hover': { backgroundColor: 'transparent', color: '#374151' } }}
                        >
                          <Box component="img" src="/images/eye.svg" alt="View icon" sx={{ width: 20, height: 20 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(doc)}
                          sx={{ color: '#6b7280', '&:hover': { backgroundColor: 'transparent', color: '#374151' } }}
                        >
                          <Box component="img" src="/images/Download.svg" alt="Download icon" sx={{ width: 20, height: 20 }} />
                        </IconButton>
                      </Box>
                    );
                    const countersignedBy = countersigned ? uploaderName(countersigned) : '';
                    return (
                      <Box>
                        {supplierNda
                          ? renderNdaRow(
                            supplierNda,
                            'NDA – signed by supplier',
                            supplierNda.fileSize ? `PDF • ${formatFileSize(supplierNda.fileSize)}` : 'Signed NDA'
                          )
                          : (
                            <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 1.5 }}>
                              The supplier has not uploaded a signed NDA.
                            </Typography>
                          )}
                        {countersigned
                          ? renderNdaRow(
                            countersigned,
                            'NDA – countersigned by Betika',
                            [
                              countersigned.fileSize ? `PDF • ${formatFileSize(countersigned.fileSize)}` : 'PDF',
                              countersignedBy ? `Uploaded by ${countersignedBy}` : '',
                              countersigned.uploadedAt ? new Date(countersigned.uploadedAt).toLocaleString() : '',
                            ].filter(Boolean).join(' • ')
                          )
                          : (
                            <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 1.5 }}>
                              No countersigned copy uploaded yet.
                            </Typography>
                          )}
                        {canUpload && (
                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            disabled={ndaUploading}
                            startIcon={ndaUploading ? <CircularProgress size={14} /> : <UploadFileOutlined />}
                            sx={{ textTransform: 'none' }}
                          >
                            {countersigned ? 'Replace countersigned NDA' : 'Upload countersigned NDA'}
                            <input
                              hidden
                              type="file"
                              accept="application/pdf,.pdf"
                              onChange={handleCountersignedNdaUpload}
                            />
                          </Button>
                        )}
                      </Box>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Contract Comments: the contract's thread, for staff who have no way into Contract Management */}
            {showsContractComments(user, supplier) && (
              <Accordion
                expanded={expanded.includes('contractComments')}
                onChange={handleAccordionChange('contractComments')}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderRadius: '0 !important',
                  mb: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    expanded.includes('contractComments') ? (
                      <ExpandMore sx={{ color: '#6b7280' }} />
                    ) : (
                      <ChevronRight sx={{ color: '#6b7280' }} />
                    )
                  }
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    minHeight: '56px',
                    '&.Mui-expanded': { minHeight: '56px', borderBottom: 'none', pb: 2 },
                    '& .MuiAccordionSummary-content': { my: 0 },
                    position: 'relative',
                    '&.Mui-expanded::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: { xs: '16px', sm: '24px' },
                      right: { xs: '16px', sm: '24px' },
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      zIndex: 1,
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                    Contract Comments
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Notes from upload
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: supplier.contract.notes ? '#111827' : '#6b7280', whiteSpace: 'pre-wrap' }}>
                      {supplier.contract.notes || 'No notes were added at upload.'}
                    </Typography>
                  </Box>
                  {/* Mounted only while open, so the timeline loads when someone looks at it */}
                  {expanded.includes('contractComments') && (
                    <ContractActivity contractId={supplier.contract._id} />
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Approval History */}
            {supplier.approvalHistory && supplier.approvalHistory.length > 0 && (
              <Accordion
                expanded={expanded.includes('approvalHistory')}
                onChange={handleAccordionChange('approvalHistory')}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderRadius: '0 !important',
                  mb: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    expanded.includes('approvalHistory') ? (
                      <ExpandMore sx={{ color: '#6b7280' }} />
                    ) : (
                      <ChevronRight sx={{ color: '#6b7280' }} />
                    )
                  }
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    minHeight: '56px',
                    '&.Mui-expanded': {
                      minHeight: '56px',
                      borderBottom: 'none',
                      pb: 2,
                    },
                    '& .MuiAccordionSummary-content': {
                      my: 0,
                    },
                    position: 'relative',
                    '&.Mui-expanded::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: { xs: '16px', sm: '24px' },
                      right: { xs: '16px', sm: '24px' },
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      zIndex: 1,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <History sx={{ color: '#6b7280', fontSize: '20px' }} />
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#111827',
                      }}
                    >
                      Approval History
                    </Typography>
                    <Chip
                      label={supplier.approvalHistory.filter(h => h.comments).length}
                      size="small"
                      sx={{
                        bgcolor: '#f3f4f6',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: 500,
                        height: '20px',
                        minWidth: '20px',
                        '& .MuiChip-label': {
                          px: 0.75,
                        },
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[...supplier.approvalHistory]
                      .filter(entry => entry.comments)
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((entry, index) => {
                        const actionConfig = {
                          approved: { label: 'Approved', color: '#10b981', bgColor: '#ecfdf5' },
                          rejected: { label: 'Rejected', color: '#ef4444', bgColor: '#fef2f2' },
                          requested_info: { label: 'Requested Info', color: '#6b7280', bgColor: '#f3f4f6' },
                          assigned_vendor_number: { label: 'Vendor Number Assigned', color: '#3b82f6', bgColor: '#eff6ff' },
                          contract_uploaded: { label: 'Contract Uploaded', color: '#8b5cf6', bgColor: '#f5f3ff' },
                          recalled: { label: 'Recalled from Rejected', color: '#b45309', bgColor: '#fffbeb' },
                          contract_applicable: { label: 'Contract Applicable', color: '#8b5cf6', bgColor: '#f5f3ff' },
                          contract_not_applicable: { label: 'Contract Not Applicable', color: '#10b981', bgColor: '#ecfdf5' },
                          entity_type_changed: { label: 'Entity Type Changed', color: '#b45309', bgColor: '#fffbeb' },
                          document_rejection_recalled: { label: 'Document Rejection Recalled', color: '#b45309', bgColor: '#fffbeb' },
                        };
                        const config = actionConfig[entry.action] || { label: entry.action, color: '#6b7280', bgColor: '#f3f4f6' };
                        const approverName = entry.approver
                          ? `${entry.approver.firstName || ''} ${entry.approver.lastName || ''}`.trim()
                          : 'System';
                        const approverRole = entry.approver?.role
                          ? entry.approver.role.charAt(0).toUpperCase() + entry.approver.role.slice(1)
                          : '';
                        const timestamp = entry.timestamp
                          ? new Date(entry.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : '';

                        return (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                                  {approverName}
                                </Typography>
                                {approverRole && (
                                  <Chip
                                    label={approverRole}
                                    size="small"
                                    sx={{
                                      bgcolor: '#f3f4f6',
                                      color: '#374151',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                      height: '20px',
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                )}
                                <Chip
                                  label={config.label}
                                  size="small"
                                  sx={{
                                    bgcolor: config.bgColor,
                                    color: config.color,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    height: '20px',
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              </Box>
                              <Typography sx={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                                {timestamp}
                              </Typography>
                            </Box>
                            {entry.comments && (
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#374151',
                                  lineHeight: 1.6,
                                  mt: 0.5,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {entry.comments}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Grid>

      </Grid>


      {/* Mobile Action Buttons - Part of form layout */}
      {canPickUp() && (
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, px: 2, pt: 3 }}>
          <Button
            variant="outlined"
            onClick={handlePickUp}
            fullWidth
            sx={{
              borderColor: '#578A18',
              color: '#578A18',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              py: 1.5,
              borderRadius: '6px',
              '&:hover': { backgroundColor: '#f0fdf4', borderColor: '#467014' },
            }}
          >
            Pick Up Task
          </Button>
        </Box>
      )}
      {showActions && (
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            gap: 1.5,
            px: 2,
            pt: 3,
            pb: 3,
            bgcolor: '#fff',
          }}
        >
          <Button
            variant="contained"
            startIcon={<Check sx={{ color: '#fff' }} />}
            onClick={() => setActionDialog({ open: true, type: 'approve' })}
            fullWidth
            sx={{
              backgroundColor: theme.palette.green.main,
              color: '#fff',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              py: 1.5,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.green.hover,
                boxShadow: 'none',
              },
              '& .MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            Approve Application
          </Button>
          <Button
            variant="contained"
            startIcon={<Close sx={{ color: '#fff' }} />}
            onClick={() => setActionDialog({ open: true, type: 'reject' })}
            fullWidth
            sx={{
              backgroundColor: '#ef4444',
              color: '#fff',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              py: 1.5,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#dc2626',
                boxShadow: 'none',
              },
              '& .MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            Reject Application
          </Button>
          <Button
            variant="outlined"
            startIcon={<ChatBubbleOutline />}
            onClick={() => setActionDialog({ open: true, type: 'request_info' })}
            fullWidth
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              py: 1.5,
              borderRadius: '6px',
              bgcolor: '#fff',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb',
              },
            }}
          >
            Request More Information
          </Button>
        </Box>
      )}

      {/* Reject Document Dialog */}
      <Dialog
        open={rejectDocDialog.open}
        onClose={() => !rejectDocSaving && setRejectDocDialog({ open: false, doc: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Reject {rejectDocDialog.doc ? formatDocumentType(rejectDocDialog.doc.documentType) : 'document'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 2 }}>
            The supplier sees this reason and nothing else, and the application
            returns to you once they upload a replacement.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="What is wrong with it?"
            value={rejectDocReason}
            onChange={(e) => setRejectDocReason(e.target.value)}
            disabled={rejectDocSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectDocDialog({ open: false, doc: null })}
            disabled={rejectDocSaving}
            sx={{ textTransform: 'none', color: '#374151' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectDocument}
            disabled={!rejectDocReason.trim() || rejectDocSaving}
            sx={{
              textTransform: 'none',
              bgcolor: '#b91c1c',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#991b1b', boxShadow: 'none' },
            }}
          >
            {rejectDocSaving ? 'Rejecting…' : 'Reject and notify supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, color: '#111827', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {fileViewerName}
          <IconButton onClick={handleCloseFileViewer} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#f5f5f5' }}>
          {fileViewerUrl && (
            <Box sx={{ width: '100%', height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {fileViewerName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img src={fileViewerUrl} alt={fileViewerName} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              ) : (
                <iframe src={fileViewerUrl} style={{ width: '100%', height: '100%', border: 'none' }} title={fileViewerName} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseFileViewer} sx={{ textTransform: 'none', color: '#6b7280' }}>Close</Button>
          <Button variant="contained" onClick={() => window.open(fileViewerUrl, '_blank')} sx={{ textTransform: 'none' }}>Open in New Tab</Button>
        </DialogActions>
      </Dialog>

      {/* Recall Document Rejection Dialog */}
      <Dialog
        open={recallDocDialog.open}
        onClose={() => !recallDocSaving && setRecallDocDialog({ open: false, doc: null, reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Recall rejection of {recallDocDialog.doc ? formatDocumentType(recallDocDialog.doc.documentType) : 'document'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
            The document is accepted as it was uploaded, and the supplier is told they don't need to upload it again.
            If this rejection is the only thing the application was returned for, it comes back for review.
          </Typography>
          {recallDocDialog.doc?.notes && (
            <Typography sx={{ fontSize: '13px', color: '#b91c1c', mb: 2 }}>
              Rejected for: {recallDocDialog.doc.notes}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason"
            required
            value={recallDocDialog.reason}
            onChange={(e) => setRecallDocDialog((d) => ({ ...d, reason: e.target.value }))}
            placeholder="For example: exception granted, CR12 accepted up to six months old"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setRecallDocDialog({ open: false, doc: null, reason: '' })}
            disabled={recallDocSaving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecallDocRejection}
            disabled={recallDocSaving || !recallDocDialog.reason.trim()}
            sx={{ textTransform: 'none' }}
          >
            {recallDocSaving ? 'Saving…' : 'Accept document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Entity Type Dialog */}
      <Dialog
        open={entityTypeDialog.open}
        onClose={() => !entityTypeSaving && setEntityTypeDialog({ open: false, entityType: '', reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Change Entity Type</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
            The entity type decides which documents the supplier must provide. Any document the new type needs
            that the supplier hasn't provided will show as missing, and the supplier is told what is still needed.
            Return the application to collect them.
          </Typography>
          <TextField
            select
            fullWidth
            label="Entity type"
            value={entityTypeDialog.entityType}
            onChange={(e) => setEntityTypeDialog((d) => ({ ...d, entityType: e.target.value }))}
            sx={{ mb: 2 }}
          >
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason"
            required
            value={entityTypeDialog.reason}
            onChange={(e) => setEntityTypeDialog((d) => ({ ...d, reason: e.target.value }))}
            placeholder="Why is the entity type being changed?"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setEntityTypeDialog({ open: false, entityType: '', reason: '' })}
            disabled={entityTypeSaving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeEntityType}
            disabled={
              entityTypeSaving
              || !entityTypeDialog.reason.trim()
              || !entityTypeDialog.entityType
              || entityTypeDialog.entityType === supplier.entityType
            }
            sx={{ textTransform: 'none' }}
          >
            {entityTypeSaving ? 'Saving…' : 'Change entity type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialogs */}
      <Dialog
        open={actionDialog.open}
        onClose={() => {
          setActionDialog({ open: false, type: '' });
          setComments('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: '16px 16px 0 0', sm: '8px' },
            margin: { xs: 0, sm: 'auto' },
            maxHeight: { xs: '90vh', sm: 'auto' },
            position: { xs: 'fixed', sm: 'relative' },
            bottom: { xs: 0, sm: 'auto' },
            top: { xs: 'auto', sm: 'auto' },
            width: { xs: '100%', sm: '500px' },
            maxWidth: { xs: '100%', sm: '500px' },
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#111827',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
          }}
        >
          <span>
            {actionDialog.type === 'approve' && 'Approve Application'}
            {actionDialog.type === 'reject' && 'Reject Application'}
            {actionDialog.type === 'request_info' && 'Request More Information'}
            {actionDialog.type === 'recall' && 'Recall Application'}
            {actionDialog.type === 'contract_applicable' && 'Contract Applicable'}
            {actionDialog.type === 'contract_not_applicable' && 'Contract Not Applicable'}
          </span>
          <IconButton
            onClick={() => {
              setActionDialog({ open: false, type: '' });
              setComments('');
            }}
            size="small"
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#6b7280',
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            {actionDialog.type === 'approve'
              ? 'Add comment to complete approval. Click submit when you\'re done.'
              : actionDialog.type === 'reject'
                ? 'Add comment to complete rejection. Click submit when you\'re done.'
                : actionDialog.type === 'recall'
                  ? `This withdraws the rejection and returns the application to ${supplier.currentApprovalStage === 'legal' ? 'legal' : 'procurement'} review. The supplier is told it is being reviewed again. Say why you are recalling it.`
                  : actionDialog.type === 'contract_not_applicable'
                    ? 'This supplier does not need a contract. Onboarding is completed now and the supplier is told. Say why a contract is not applicable.'
                    : actionDialog.type === 'contract_applicable'
                      ? (supplier.status === 'completed'
                        ? 'This returns the supplier to the contract upload queue, so a contract can be uploaded. Add a comment if you like.'
                        : 'A contract will be uploaded for this supplier. Add a comment if you like.')
                  : 'Add comment to describe what additional information is required. Click submit when you\'re done.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Write a comment here"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: '#d1d5db',
                },
                '&:hover fieldset': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused fieldset': {
                  borderColor:
                    actionDialog.type === 'approve'
                      ? theme.palette.green.main
                      : actionDialog.type === 'reject'
                        ? '#ef4444'
                        : '#6b7280',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                color: '#111827',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: { xs: 4, sm: 3 }, pt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              if (actionDialog.type === 'approve') handleApprove();
              else if (actionDialog.type === 'reject') handleReject();
              else if (actionDialog.type === 'request_info') handleRequestInfo();
              else if (actionDialog.type === 'recall') handleRecall();
              else if (actionDialog.type === 'contract_applicable') handleContractApplicability('applicable');
              else if (actionDialog.type === 'contract_not_applicable') handleContractApplicability('not_applicable');
            }}
            // The only decision here that needs no reason: applicable leaves the
            // workflow where it was going anyway.
            disabled={!comments.trim() && actionDialog.type !== 'contract_applicable'}
            sx={{
              backgroundColor:
                actionDialog.type === 'approve'
                  ? theme.palette.green.main
                  : actionDialog.type === 'reject'
                    ? '#ef4444'
                    : '#6b7280',
              color: '#fff',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              py: 1.5,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor:
                  actionDialog.type === 'approve'
                    ? theme.palette.green.hover
                    : actionDialog.type === 'reject'
                      ? '#dc2626'
                      : '#4b5563',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor:
                  actionDialog.type === 'approve'
                    ? '#a8b89a' // Green with grey shade
                    : actionDialog.type === 'reject'
                      ? '#fca5a5' // Red with grey shade
                      : '#d1d5db', // Grey
                color: '#fff',
              },
            }}
          >
            {actionDialog.type === 'approve'
              ? 'Submit Approval'
              : actionDialog.type === 'reject'
                ? 'Submit Rejection'
                : 'Submit Request for More Information'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reminder — tells the supplier what is still outstanding. The message
          itself is built on the server; staff only add an optional note. */}
      <Dialog
        open={remindOpen}
        onClose={() => !reminding && !previewing && closeRemind()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          {remindPreview ? 'Preview the reminder' : 'Remind the supplier to finish their application'}
        </DialogTitle>
        <DialogContent>
          {!remindPreview ? (
            <>
              <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 2 }}>
                {supplier?.status === 'more_info_required'
                  ? 'They will be sent the information request again, with how long ago it was made.'
                  : 'They will be sent the list of what is still missing from their draft.'}
                {' '}You will see the email before it is sent. One reminder can be sent per supplier per day.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Note (optional)"
                placeholder="Added under your name"
                value={remindNote}
                onChange={(e) => setRemindNote(e.target.value.slice(0, 500))}
                helperText={`${remindNote.length}/500`}
              />
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '14px', color: '#374151', mb: 0.5 }}>
                <strong>To:</strong> {remindPreview.to || 'No email address on file — the supplier is notified in the portal only'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#374151', mb: 2 }}>
                <strong>Subject:</strong> {remindPreview.title}
              </Typography>
              {remindPreview.nextReminderAt && (
                <Typography sx={{ fontSize: '14px', color: '#b45309', fontWeight: 500, mb: 2 }}>
                  This supplier was reminded in the last 24 hours. Another reminder can be sent after{' '}
                  {new Date(remindPreview.nextReminderAt).toLocaleString()}.
                </Typography>
              )}
              {/* The email exactly as it will go out, rendered by the server's
                  own template. sandbox with no permissions: it is shown, never run. */}
              <Box
                component="iframe"
                title="Reminder email preview"
                sandbox=""
                srcDoc={remindPreview.html}
                sx={{ width: '100%', height: 440, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#fff' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {remindPreview ? (
            <Button
              onClick={() => setRemindPreview(null)}
              disabled={reminding}
              sx={{ textTransform: 'none', color: '#6b7280' }}
            >
              Back
            </Button>
          ) : (
            <Button
              onClick={closeRemind}
              disabled={previewing}
              sx={{ textTransform: 'none', color: '#6b7280' }}
            >
              Cancel
            </Button>
          )}
          {remindPreview ? (
            <Button
              variant="contained"
              disableElevation
              onClick={handleSendReminder}
              disabled={reminding || Boolean(remindPreview.nextReminderAt)}
              sx={{ textTransform: 'none', backgroundColor: '#578A18', '&:hover': { backgroundColor: '#467014' } }}
            >
              {reminding ? 'Sending…' : 'Send Reminder'}
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={handlePreviewReminder}
              disabled={previewing}
              sx={{ textTransform: 'none', backgroundColor: '#578A18', '&:hover': { backgroundColor: '#467014' } }}
            >
              {previewing ? 'Preparing preview…' : 'Preview'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — super admin, or procurement while allowed. Requires a reason, which is
          recorded in the audit log, and the supplier name typed back. */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, color: '#b91c1c' }}>
          Delete this application permanently?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#374151', mb: 2 }}>
            This removes <strong>{supplier?.supplierName?.trim() || 'this unnamed application'}</strong>,
            every document uploaded against it, the files on disk, and any draft contract
            records. It cannot be undone.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 2 }}>
            The supplier's user account is kept, so their other applications and the audit
            history are preserved. Deactivate the account separately if that is intended.
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason for deletion"
            placeholder="Recorded in the audit log"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={`Type "${deleteConfirmPhrase}" to confirm`}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{ textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={handleDeleteSupplier}
            disabled={
              deleting ||
              !deleteReason.trim() ||
              deleteConfirmName.trim() !== deleteConfirmPhrase
            }
            sx={{ textTransform: 'none' }}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SupplierDetails;
